import { useMemo, useState } from 'react';

import type { LinkPickerPlugin } from '@atlaskit/link-picker/types';

import AtlassianLinkPickerPlugin, { type AtlassianLinkPickerPluginConfig } from '../../main';

/**
 * Hook that takes in a array of plugin configs and creates a new list of corresponding AtlassianLinkPickerPlugin instances.
 * If a particular plugin config does not change, the same plugin instance will be returned.
 *
 * The order of configs is not changed from the input.
 */
export const useAtlassianPlugins = (
	configs: AtlassianLinkPickerPluginConfig[],
): LinkPickerPlugin[] => {
	const [atlassianPlugins] = useState(
		() => new WeakMap<AtlassianLinkPickerPluginConfig, AtlassianLinkPickerPlugin>(),
	);

	return useMemo(
		() =>
			configs.map((config) => {
				const existingPlugin = atlassianPlugins.get(config);
				if (existingPlugin) {
					return existingPlugin;
				}

				// If we haven't already created a plugin for this config, create one now
				// and store it in the map.
				const newPlugin = new AtlassianLinkPickerPlugin(config);
				atlassianPlugins.set(config, newPlugin);
				return newPlugin;
			}),
		[atlassianPlugins, configs],
	);
};
